import { useCallback } from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CapabilityListField } from '@/components/admin/CapabilityListField';
import type {
  EntitlementRule,
  CompositeEntitlementRule,
} from '@/api/auth/missionImpactAuthAPI.schemas';

/** A single-purpose (non-composite) rule. */
type LeafEntitlementRule = Exclude<EntitlementRule, CompositeEntitlementRule>;

/** The rule type the user selects; a capability-bearing count rule becomes a composite on save. */
type SelectableRuleType = 'cumulative' | 'minimum' | 'capability';

/**
 * Props for EntitlementRuleFields component
 */
interface EntitlementRuleFieldsProps {
  /** React Hook Form setValue function */
  setValue: UseFormSetValue<any>;
  /** React Hook Form watch function */
  watch: UseFormWatch<any>;
  /** Field path prefix (e.g., 'rule' or 'entitlementRule') */
  fieldPath?: string;
  /** Validation error message to display */
  error?: string;
}

/** Expands a rule into its immediate children: a composite's rules, or the rule itself. */
function leavesOf(rule: EntitlementRule | undefined): EntitlementRule[] {
  if (!rule) return [];
  return rule.type === 'composite' ? rule.rules : [rule];
}

/**
 * Assembles a rule from the editor's separate count and capability inputs: a single leaf when only
 * one is present, and a composite when a count rule also carries capabilities.
 */
function composeRule(
  type: SelectableRuleType,
  count: number,
  capabilities: string[]
): EntitlementRule {
  if (type === 'capability') {
    return { type: 'capability', capabilities };
  }

  const countLeaf: LeafEntitlementRule =
    type === 'cumulative' ? { type: 'cumulative', count } : { type: 'minimum', minimum: count };

  if (capabilities.length === 0) {
    return countLeaf;
  }

  return { type: 'composite', rules: [countLeaf, { type: 'capability', capabilities }] };
}

/**
 * Reusable component for editing an entitlement rule.
 *
 * The user picks a count type (cumulative/minimum) or capability-only, and can add capability
 * strings to any count rule. The component reads and writes the API rule shape directly, bundling a
 * count and capabilities into a composite rule when both are present.
 */
export function EntitlementRuleFields({
  setValue,
  watch,
  fieldPath = 'rule',
  error,
}: EntitlementRuleFieldsProps) {
  const rule = watch(fieldPath) as EntitlementRule | undefined;
  const leaves = leavesOf(rule);

  const countLeaf = leaves.find((leaf) => leaf.type === 'cumulative' || leaf.type === 'minimum');
  const capabilityLeaf = leaves.find((leaf) => leaf.type === 'capability');

  const selectedType: SelectableRuleType =
    countLeaf?.type ?? (capabilityLeaf ? 'capability' : 'cumulative');
  const countValue =
    countLeaf?.type === 'cumulative'
      ? countLeaf.count
      : countLeaf?.type === 'minimum'
        ? countLeaf.minimum
        : 1;
  const capabilities = capabilityLeaf?.capabilities ?? [];

  const update = useCallback(
    (type: SelectableRuleType, count: number, caps: string[]) => {
      setValue(fieldPath, composeRule(type, count, caps), {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [setValue, fieldPath]
  );

  const isCount = selectedType === 'cumulative' || selectedType === 'minimum';
  const ruleTypeDescription = isCount
    ? selectedType === 'cumulative'
      ? 'Cumulative entitlements are additive across all sources'
      : 'Minimum entitlements define a floor that must be met'
    : 'Capability-only entitlements grant capability strings with no count';

  return (
    <>
      <div className="grid gap-2">
        <Label htmlFor="ruleType">Rule Type</Label>
        <Select
          value={selectedType}
          onValueChange={(type) => update(type as SelectableRuleType, countValue, capabilities)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a rule type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="cumulative">Cumulative (additive count)</SelectItem>
            <SelectItem value="minimum">Minimum (minimum required)</SelectItem>
            <SelectItem value="capability">Capability only (no count)</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">{ruleTypeDescription}</p>
      </div>

      {isCount && (
        <div className="grid gap-2">
          <Label htmlFor="ruleValue">{selectedType === 'cumulative' ? 'Count' : 'Minimum'}</Label>
          <Input
            id="ruleValue"
            type="number"
            min="1"
            value={countValue > 0 ? countValue : ''}
            onChange={(e) => {
              const parsed = parseInt(e.target.value, 10);
              update(selectedType, Number.isNaN(parsed) ? 0 : parsed, capabilities);
            }}
          />
        </div>
      )}

      <div className="grid gap-2">
        <Label>Capabilities{isCount ? ' (optional)' : ''}</Label>
        <CapabilityListField
          capabilities={capabilities}
          onChange={(next) => update(selectedType, countValue, next)}
          error={error}
        />
      </div>
    </>
  );
}
